import React, { useMemo, useState } from 'react';

import {
  AddEmployeesIcon,
  Box,
  Button,
  ButtonMode,
  LoadingSpinner,
  PagePanel,
  SetBenefitsIcon
} from 'edenred-ui';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { goToPage } from '@epi-actions/navigation';
import { useGetCompanyBeneficiariesQuery } from '@epi-api/companyBeneficiaries';
import OnboardingStepper from '@epi-components/OnboardingStepper/OnboardingStepper';
import { OnboardingSupportInformation } from '@epi-components/OnboardingSupportInformation';
import { routerPaths } from '@epi-constants/routerPaths';
import { useManageBenefits } from '@epi-containers/ManageBenefits/hooks';
import { useGetBenefitSettingsGroupSettingsQuery } from '@epi-api/settings/settingsApi';
import { EmptyState, Pagination } from '@epi-shared/components';
import { useOnboardingWizard } from '@epi-shared/hooks';
import { useContractedBenefits } from '@epi-shared/hooks/useContractedBenefits';
import {
  BeneficiaryItemsPerPage,
  SortByLastName
} from '@epi-constants/beneficiary';
import { sliceRows, sortRows } from '@epi-helpers/SortHelpers';

import { BeneficiariesTable } from './components/BeneficiariesTable';
import { BeneficiariesTopSection } from './components/BeneficiariesTopSection';
import BeneficiariesModal from './components/BeneficiariesModal/BeneficiariesModal';

const BeneficiariesList = () => {
  const { t } = useTranslation();
  const [showBeneficiaryModal, setshowBeneficiaryModal] = useState(false);
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState('');
  const [isEmployeeAdded, setIsEmployeeAdded] = useState(false);
  const contractedBenefits = useContractedBenefits();
  const { isLunchBenefitActive } = useManageBenefits();

  const { data: settings, isLoading: isSettingsLoading } =
    useGetBenefitSettingsGroupSettingsQuery();

  const { isLoading: isGetEmployeesLoading, data: employees } =
    useGetCompanyBeneficiariesQuery();
  const reduxDispatch = useDispatch();
  const {
    isOnBoardingActive,
    flags,
    isLoading: isOnboardingLoading,
    handleGoOnboardingPayment
  } = useOnboardingWizard();

  const paginationCount = Math.ceil(
    employees?.beneficiaries.length
      ? employees?.beneficiaries.length / BeneficiaryItemsPerPage
      : 0
  );

  const [page, setPage] = useState(1);

  const sortedEmployees = useMemo(() => {
    if (employees?.beneficiaries.length) {
      const sorted = sortRows(employees?.beneficiaries, false, SortByLastName);
      return sliceRows(sorted, page, BeneficiaryItemsPerPage);
    }
    return [];
  }, [employees?.beneficiaries, page]);

  const showBeneficiaryAddedNotification =
    isEmployeeAdded &&
    isOnBoardingActive &&
    employees?.beneficiaries.some(b => b.isNew === true);

  const showDetail = (beneficiaryId: string) => {
    setSelectedBeneficiaryId(beneficiaryId);
    setshowBeneficiaryModal(true);
  };

  const handleClose = () => {
    setshowBeneficiaryModal(false);
    setSelectedBeneficiaryId('');
  };
  const footer = isOnBoardingActive
    ? {
        backText: t('components.onboarding.buttons.back'),
        submitText: t('components.onboarding.buttons.next'),
        onBack: () => {
          reduxDispatch(goToPage(routerPaths.manageBenefits));
        },
        onSubmit: handleGoOnboardingPayment,
        disableSubmit: !flags?.isEmployeesSet,
        submitButtonWidth: '240px'
      }
    : undefined;

  const renderPagePanel = () => {
    if (isGetEmployeesLoading || isSettingsLoading || isOnboardingLoading) {
      return <LoadingSpinner />;
    }

    if (!settings?.length) {
      return (
        <EmptyState
          icon={<SetBenefitsIcon />}
          title={t('employees.set_benefits_title')}
          description={t('employees.set_benefits_description')}
          btnText={t('employees.set_benefits_btn')}
          btnOnClick={() => reduxDispatch(goToPage(routerPaths.manageBenefits))}
        />
      );
    }

    if (!sortedEmployees.length) {
      return (
        <EmptyState
          icon={<AddEmployeesIcon />}
          title={t('employees.no_employees_title')}
          description={t('employees.no_employees_description')}
          btnText={t('employees.no_employees_btn')}
          btnOnClick={() => setshowBeneficiaryModal(true)}
        />
      );
    }

    return (
      <div>
        {showBeneficiaryAddedNotification && <BeneficiariesTopSection />}
        <BeneficiariesTable
          beneficiaries={sortedEmployees}
          isLoading={isGetEmployeesLoading}
          showDetail={showDetail}
          contractedBenefits={contractedBenefits}
        />
        {employees?.beneficiaries &&
          employees.beneficiaries.length > BeneficiaryItemsPerPage && (
            <Pagination
              page={page}
              count={paginationCount}
              defaultRowsPerPage={BeneficiaryItemsPerPage}
              onChangePage={page => setPage(page)}
            />
          )}
      </div>
    );
  };

  return (
    <Box>
      <OnboardingStepper />
      <PagePanel
        title={t('containers.beneficiaries.title')}
        headerContent={
          !!settings?.length && (
            <Button
              mode={ButtonMode.Secondary}
              onClick={() => {
                setshowBeneficiaryModal(true);
              }}
              id="add-employee-btn"
            >
              {t('containers.beneficiaries.add_employee.trigger')}
            </Button>
          )
        }
        footer={footer}
      >
        <Box minHeight={570}>{renderPagePanel()}</Box>
      </PagePanel>
      <OnboardingSupportInformation />

      <BeneficiariesModal
        isShowModal={showBeneficiaryModal}
        benefitGroupSettings={settings}
        isLunchBenefitActive={isLunchBenefitActive}
        beneficiaryId={selectedBeneficiaryId}
        onClose={handleClose}
        setIsEmployeeAdded={setIsEmployeeAdded}
      />
    </Box>
  );
};

export default BeneficiariesList;
